import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Navbar, Footer } from "@/components/yc/Nav";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const STEPS = [
    { n: "01", t: "We vet every creative", d: "Portfolio, source files, rate card and a signed originality declaration. Then we score them.", img: 0 },
    { n: "02", t: "Choose your seats and get trained", d: "Pick a plan with a seat for each person who hires. We set your team up and train you, free.", img: 1 },
    { n: "03", t: "Search and shortlist", d: "Filter by discipline, location, rate, availability and score.", img: 2 },
    { n: "04", t: "Unlock what you need", d: "Use credits to reveal contact details, hiring insights and reliability data.", credits: true },
    { n: "05", t: "Hire and rate", d: "Hire your creative directly, with no agency mark-up or commission. Then rate them: their ratings keep Health Scores honest.", img: 3 },
];
const CREDIT_ROWS = ["Telephone number", "Email address", "Hiring insights", "Job turnover", "Reliability"];
const FAQS = [
    { q: "Who is Curated Chaos for?", a: "In-house marketing teams, agencies and recruiters who need vetted UK creative talent without the gamble of freelance marketplaces." },
    { q: "What is a seat?", a: "A seat is a named login for one person on your team who hires. Each plan includes a set number of seats, and the account owner can invite, remove and reassign them." },
    { q: "Do you take commission when we hire?", a: "No. You hire creatives directly with no agency mark-up or commission." },
    { q: "How are creatives vetted?", a: "Every creative submits a portfolio, source files, a rate card and a signed originality declaration, then we score them before they go live." },
    { q: "What are credits?", a: "Credits unlock creator data such as telephone, email, hiring insights, job turnover and reliability. They are shared by your team and refresh every month." },
    { q: "Is there a free plan?", a: "Creatives join free until 30 April 2027. Brands choose an annual plan with seats and monthly credits, and our team will help you pick the right one." },
    { q: "Do you allow AI-generated work?", a: "No. Every creative signs an originality declaration and we check portfolios against original source files." },
    { q: "How much does it cost for creatives?", a: "Creatives join free until 30 April 2027." },
];

// Hero: 5 creator portraits on a continuous loop
const HERO_IMAGES = [
    "https://customer-assets-v7afamib.emergentagent.net/job_vetted-hub-build/artifacts/r252bcjq_1.webp",
    "https://customer-assets-v7afamib.emergentagent.net/job_vetted-hub-build/artifacts/kx2va6vc_2.webp",
    "https://customer-assets-v7afamib.emergentagent.net/job_vetted-hub-build/artifacts/nm9saott_3.webp",
    "https://customer-assets-v7afamib.emergentagent.net/job_vetted-hub-build/artifacts/h3cuidbs_4.webp",
    "https://customer-assets-v7afamib.emergentagent.net/job_vetted-hub-build/artifacts/0kh9kfmi_5.webp",
];
// One confidence score per slide — always within 85–97
const CONFIDENCE_SCORES = [91, 86, 97, 88, 94];

const Landing = () => {
    const [slide, setSlide] = useState(0);
    const [plans, setPlans] = useState([]);
    const [founding, setFounding] = useState(null);
    useEffect(() => {
        const t = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 3500);
        return () => clearInterval(t);
    }, []);
    useEffect(() => {
        api.get("/public/plans").then((r) => { setPlans(r.data.plans || []); setFounding(r.data.founding || null); }).catch(() => {});
    }, []);

    return (
        <div className="min-h-screen bg-yc-cream">
            <Navbar />

            {/* HERO */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-12 pt-16 md:pt-24 pb-12">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-7">
                        <div className="inline-flex items-center gap-2 yc-badge bg-yc-pink/15 text-yc-pink">
                            Curated network access for businesses
                        </div>
                        <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] mt-6 text-yc-ink">
                            The UK's first <span className="text-yc-blue">vetted</span> creative talent network.
                        </h1>
                        <p className="text-lg md:text-xl text-yc-ink/80 mt-8 leading-relaxed max-w-2xl">
                            Ready to build or add to your marketing team? Choose from over 200 vetted creatives, including graphic designers, illustrators, animators, photographers and content creators.
                        </p>
                        <ul className="mt-8 space-y-3 text-base md:text-lg text-yc-ink/90">
                            <li className="flex items-center gap-3" data-testid="hero-point-1">
                                <CheckCircle2 className="w-5 h-5 text-yc-blue shrink-0" />
                                <span><strong>200+</strong> vetted creatives ready to hire</span>
                            </li>
                            <li className="flex items-center gap-3" data-testid="hero-point-2">
                                <CheckCircle2 className="w-5 h-5 text-yc-blue shrink-0" />
                                <span>Available in <strong>every major UK city</strong></span>
                            </li>
                            <li className="flex items-center gap-3" data-testid="hero-point-3">
                                <CheckCircle2 className="w-5 h-5 text-yc-blue shrink-0" />
                                <span>Every creative <strong>scored</strong> before they join</span>
                            </li>
                        </ul>
                        <div className="flex flex-wrap gap-4 mt-10">
                            <Link to="/brands/join" data-testid="hero-join-brand" className="yc-btn-primary">
                                Apply now <ArrowRight className="ml-2 w-5 h-5" />
                            </Link>
                            <Link to="/pricing" data-testid="hero-see-pricing" className="yc-btn-secondary">
                                See plans &amp; pricing
                            </Link>
                        </div>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="yc-card relative overflow-hidden" data-testid="hero-carousel">
                            <div className="relative w-full h-[460px]">
                                {HERO_IMAGES.map((src, i) => (
                                    <img
                                        key={src}
                                        src={src}
                                        alt={`Curated Chaos creator ${i + 1}`}
                                        loading={i === 0 ? "eager" : "lazy"}
                                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
                                        style={{ opacity: slide === i ? 1 : 0 }}
                                    />
                                ))}
                            </div>
                            <div className="absolute top-4 left-4 yc-badge bg-yc-pink text-white">Premium content, on tap</div>
                            <div className="absolute bottom-4 right-4 yc-card-sm p-4 max-w-[180px]">
                                <div className="yc-overline">Confidence Index</div>
                                <div className="font-heading font-black text-3xl mt-1" data-testid="confidence-index">{CONFIDENCE_SCORES[slide]}<span className="text-base">/100</span></div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* MARQUEE */}
            <div className="border-y-2 border-yc-ink bg-yc-blue text-white py-6 overflow-hidden">
                <div className="flex whitespace-nowrap animate-marquee font-heading font-black text-3xl md:text-5xl uppercase tracking-wider">
                    {Array.from({ length: 8 }).map((_, i) => (
                        <span key={`marquee-${i}`} className="px-8">VETTED · TRUSTED · DELIVERED · </span>
                    ))}
                </div>
            </div>

            {/* EXPLORE — audience cards */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 pt-20 md:pt-28 pb-10">
                <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mb-8">Explore Curated Chaos for you</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
                    {[
                        { k: "In-house marketing teams", t: "Fill briefs fast with creatives you can trust", to: "/brands/join", cta: "Find out more", id: "explore-inhouse" },
                        { k: "Agencies", t: "Scale your studio without adding headcount", to: "/brands/join", cta: "Find out more", id: "explore-agencies" },
                        { k: "Creative recruiters", t: "A pre-qualified shortlist, not a pile of CVs", to: "/brands/join", cta: "Find out more", id: "explore-recruiters" },
                    ].map((c) => (
                        <Link key={c.id} to={c.to} data-testid={c.id} className="yc-card p-6 bg-yc-pink/10 flex flex-col justify-between hover:-translate-y-1 hover:shadow-yc-lg transition-all min-h-[200px]">
                            <div>
                                <div className="yc-overline text-yc-pink">{c.k}</div>
                                <h3 className="font-heading text-xl font-bold tracking-tight mt-3 leading-snug">{c.t}</h3>
                            </div>
                            <span className="text-sm font-semibold mt-6 inline-flex items-center gap-1">{c.cta} <ArrowRight className="w-4 h-4" /></span>
                        </Link>
                    ))}
                    <Link to="/creators" data-testid="explore-creatives" className="yc-card p-6 bg-yc-ink text-white flex flex-col justify-between hover:-translate-y-1 hover:shadow-yc-lg transition-all min-h-[200px]">
                        <div>
                            <div className="yc-overline text-yc-pink">Creatives</div>
                            <h3 className="font-heading text-xl font-bold tracking-tight mt-3 leading-snug">Get found by brands. Free to join until 30 April 2027</h3>
                        </div>
                        <span className="text-sm font-semibold mt-6 inline-flex items-center gap-1">Apply as a creative <ArrowRight className="w-4 h-4" /></span>
                    </Link>
                </div>
            </section>

            {/* GAMBLE — usual way vs Curated Chaos way */}
            <section className="bg-yc-pink/5 border-y-2 border-yc-ink/5 py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">Hiring creatives shouldn't be a gamble.</h2>
                        <p className="text-yc-ink/70 mt-3">Here's the difference a vetted, scored network makes.</p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
                        <div className="yc-card p-8" data-testid="usual-way">
                            <h3 className="font-heading text-xl font-bold tracking-tight">The usual way</h3>
                            <ul className="mt-5">
                                {[
                                    "Sifting hundreds of portfolios",
                                    "No idea if they'll deliver on time",
                                    "AI-generated work passed off as original",
                                    "Rates unclear until the third email",
                                    "Weeks to fill a brief",
                                    "Hoping it works out",
                                ].map((x) => (
                                    <li key={x} className="py-3 border-b border-yc-ink/10 last:border-0 text-yc-ink/80">{x}</li>
                                ))}
                            </ul>
                        </div>
                        <div className="yc-card p-8 bg-yc-blue text-white border-yc-ink" data-testid="curated-way">
                            <h3 className="font-heading text-xl font-bold tracking-tight text-white">The Curated Chaos way</h3>
                            <ul className="mt-5">
                                {[
                                    "A scored shortlist in minutes",
                                    "Reliability data from real jobs",
                                    "Every portfolio checked against source files",
                                    "Rate cards up front",
                                    "Filter by discipline, location and availability",
                                    "Curated Creator Score and Health Score on every profile",
                                ].map((x) => (
                                    <li key={x} className="py-3 border-b border-white/15 last:border-0 flex items-center gap-2"><CheckCircle2 className="w-4 h-4 shrink-0" /> {x}</li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW CURATED CHAOS WORKS */}
            <section id="how" className="max-w-7xl mx-auto px-6 md:px-12 py-20 md:py-28">
                <div className="text-center mb-16">
                    <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">How Curated Chaos works</h2>
                    <p className="text-yc-ink/70 mt-3 max-w-xl mx-auto">Your seats give your team direct access to our network. Here's how you go from search to hire.</p>
                </div>
                <div className="space-y-16">
                    {STEPS.map((s, idx) => {
                        const textLeft = idx % 2 === 1;
                        const textBlock = (
                            <div key="t">
                                <div className="font-heading font-black text-5xl md:text-6xl text-yc-blue">{s.n}</div>
                                <h3 className="font-heading text-2xl md:text-3xl font-bold tracking-tight mt-3">{s.t}</h3>
                                <p className="text-yc-ink/75 mt-3 leading-relaxed max-w-md">{s.d}</p>
                                <Link to="/brands/join" data-testid={`step-${s.n}-apply`} className="yc-btn-primary mt-6">Apply now</Link>
                            </div>
                        );
                        const mediaBlock = s.credits ? (
                            <div key="m" className="yc-card p-6" data-testid="credits-table">
                                {CREDIT_ROWS.map((r) => (
                                    <div key={r} className="flex items-center justify-between py-3 border-b border-yc-ink/10 last:border-0">
                                        <span className="text-sm text-yc-ink/80">{r}</span>
                                        <span className="text-sm font-semibold text-yc-blue">2 credits</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div key="m" className="yc-card overflow-hidden">
                                <img src={HERO_IMAGES[s.img]} alt={s.t} loading="lazy" className="w-full h-64 md:h-72 object-cover" />
                            </div>
                        );
                        return (
                            <div key={s.n} className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-16 items-center">
                                {textLeft ? [textBlock, mediaBlock] : [mediaBlock, textBlock]}
                            </div>
                        );
                    })}
                </div>
            </section>

            {/* BUILT ON REAL RESULTS */}
            <section className="bg-yc-ink text-white py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <h2 className="text-center font-heading text-4xl md:text-5xl font-black tracking-tighter">Built on real results</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-12">
                        {[
                            { v: "200+", l: "Vetted creatives in the network" },
                            { v: "8", l: "Creative disciplines" },
                            { v: "UK wide", l: "Available in every major city" },
                        ].map((s) => (
                            <div key={s.l} className="rounded-2xl border border-white/15 bg-white/5 p-8 text-center">
                                <div className="font-heading font-black text-5xl tracking-tighter text-yc-pink">{s.v}</div>
                                <div className="text-sm text-white/70 mt-2">{s.l}</div>
                            </div>
                        ))}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5">
                        <div className="rounded-2xl bg-yc-pink p-8" data-testid="founding-card">
                            <div className="yc-overline text-white">Founding members</div>
                            <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mt-2">25% off your first year</h3>
                            <p className="text-white/90 mt-2 text-sm">Price locked for two years. Only {founding?.left ?? 20} of {founding?.total ?? 20} founding places left.</p>
                            <Link to="/brands/join" className="yc-btn-primary bg-yc-ink text-white mt-6">Apply now</Link>
                        </div>
                        <div className="rounded-2xl border border-white/15 bg-white/5 p-8">
                            <div className="yc-overline text-yc-pink">Case study</div>
                            <h3 className="font-heading text-2xl md:text-3xl font-black tracking-tighter mt-2">27.9 million location-based impressions</h3>
                            <p className="text-white/80 mt-2 text-sm">In three months, from a £750 spend.</p>
                            <Link to="/pricing" className="inline-flex items-center gap-1 mt-6 font-semibold underline underline-offset-4">Read the case study <ArrowRight className="w-4 h-4" /></Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* CHOOSE YOUR SEATS */}
            <section id="pricing" className="bg-white py-20 md:py-28">
                <div className="max-w-7xl mx-auto px-6 md:px-12">
                    <div className="text-center mb-12">
                        <h2 className="font-heading text-4xl md:text-5xl font-black tracking-tighter">Choose your seats</h2>
                        <p className="text-yc-ink/70 mt-3 max-w-2xl mx-auto">One seat per person who hires. Not sure? Our team will help you pick the right plan. All prices exclude VAT.</p>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
                        {plans.map((p) => (
                            <div key={p.id} data-testid={`home-plan-${p.id}`} className={`yc-card p-8 flex flex-col ${p.popular ? "border-yc-blue border-4" : ""}`}>
                                <div className="yc-overline text-yc-pink">{p.tagline}</div>
                                <h3 className="font-heading text-2xl font-black tracking-tighter mt-2">{p.name}</h3>
                                <div className="text-sm text-yc-ink/70 mt-2">{p.users} seats · {p.credits} credits / mo</div>
                                <div className="mt-5 font-heading flex items-baseline gap-1">
                                    {p.poa
                                        ? <span className="text-4xl md:text-5xl font-black tracking-tighter">£POA</span>
                                        : <><span className="text-4xl md:text-5xl font-black tracking-tighter">£{p.price_monthly.toLocaleString()}</span><span className="text-yc-ink/60 text-sm">/ mo</span></>}
                                </div>
                                <div className="text-sm text-yc-ink/60 mt-1">Billed annually</div>
                                <div className="mt-auto pt-6 space-y-3">
                                    <Link to="/brands/join" className="yc-btn-primary block w-full text-center">Apply now</Link>
                                    <Link to="/pricing" className="yc-btn-secondary block w-full text-center">Compare plans</Link>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GIVE YOUR TEAM A SEAT — CTA */}
            <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
                <div className="yc-card bg-yc-blue text-white border-yc-ink p-8 md:p-12 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-8">
                    <div>
                        <h2 className="font-heading text-3xl md:text-4xl font-black tracking-tighter max-w-xl">Give your team a seat in the UK's vetted creative network</h2>
                        <div className="flex flex-wrap gap-x-6 gap-y-2 mt-5 text-sm">
                            {["200+ vetted creatives", "Every major UK city", "Credits refresh monthly", "Free onboarding training"].map((x) => (
                                <span key={x} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4" /> {x}</span>
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-wrap gap-3 shrink-0">
                        <Link to="/brands/join" data-testid="cta-apply" className="yc-btn-primary bg-yc-pink text-white">Apply now</Link>
                        <Link to="/pricing" data-testid="cta-pricing" className="yc-btn-primary bg-white text-yc-ink">See plans &amp; pricing</Link>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto px-6 md:px-12 py-16 md:py-24">
                <h2 className="text-center font-heading text-4xl md:text-5xl font-black tracking-tighter mb-10">Frequently asked questions</h2>
                <Accordion type="single" collapsible className="space-y-3">
                    {FAQS.map((f, i) => (
                        <AccordionItem key={f.q} value={`faq-${i}`} data-testid={`faq-${i}`} className="yc-card px-5 border-b-0">
                            <AccordionTrigger className="text-left font-heading font-bold text-base">{f.q}</AccordionTrigger>
                            <AccordionContent className="text-yc-ink/80 text-sm leading-relaxed">{f.a}</AccordionContent>
                        </AccordionItem>
                    ))}
                </Accordion>
            </section>

            <Footer />
        </div>
    );
};

export default Landing;
